/**
 * src/app/(public)/[locale]/error.tsx
 *
 * Public runtime error boundary — locale segment'inde herhangi bir Server/Client
 * Component patladığında devreye girer.
 *
 * 'use client' zorunlu — `reset` callback'i client-side.
 *
 * Tasarım: 404 sayfasının siber güvenlik teması ile uyumlu, kırmızı accent
 * (alarm hissi), terminal command/response gösterimi (error.digest dahil).
 *
 * Sentry: SDK zaten root layout'tan yüklendi; useEffect ile manual capture.
 */
'use client'

import * as Sentry from '@sentry/nextjs'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'

import { Link } from '@/lib/i18n/navigation'
import {
  AlertOctagon,
  ArrowRight,
  Home,
  Mail,
  RotateCw,
  Terminal,
} from 'lucide-react'

interface ErrorPageProps {
  error: Error & { digest?: string }
  reset: () => void
}

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  const t = useTranslations('errors.server_error')

  useEffect(() => {
    Sentry.captureException(error)
  }, [error])

  return (
    <div className="relative min-h-[calc(100vh-160px)] overflow-hidden">
      {/* Arka plan — error tonunda (kırmızı/turuncu) */}
      <div
        aria-hidden
        className="absolute inset-0 -z-20 bg-gradient-to-b from-slate-50 via-white to-slate-100/50 dark:from-slate-950 dark:via-[#1a0a0a] dark:to-slate-950"
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.06] dark:opacity-[0.10] text-slate-900 dark:text-slate-100"
        style={{
          backgroundImage:
            'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage:
            'radial-gradient(ellipse at center, black 0%, transparent 70%)',
          WebkitMaskImage:
            'radial-gradient(ellipse at center, black 0%, transparent 70%)',
        }}
      />
      <div
        aria-hidden
        className="absolute top-20 -left-20 w-[480px] h-[480px] -z-10 rounded-full bg-rose-500/20 dark:bg-rose-700/15 blur-[140px]"
      />
      <div
        aria-hidden
        className="absolute bottom-10 -right-20 w-[480px] h-[480px] -z-10 rounded-full bg-amber-500/15 dark:bg-amber-700/15 blur-[140px]"
      />

      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="relative">
          {/* HUD corner brackets */}
          <div
            aria-hidden
            className="absolute -top-4 -left-4 w-8 h-8 border-t-2 border-l-2 border-rose-500/40 dark:border-rose-400/40"
          />
          <div
            aria-hidden
            className="absolute -top-4 -right-4 w-8 h-8 border-t-2 border-r-2 border-rose-500/40 dark:border-rose-400/40"
          />
          <div
            aria-hidden
            className="absolute -bottom-4 -left-4 w-8 h-8 border-b-2 border-l-2 border-rose-500/40 dark:border-rose-400/40"
          />
          <div
            aria-hidden
            className="absolute -bottom-4 -right-4 w-8 h-8 border-b-2 border-r-2 border-rose-500/40 dark:border-rose-400/40"
          />

          <div className="relative grid lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-12 items-center py-8 lg:py-12">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300 text-xs font-mono font-semibold">
                <AlertOctagon size={12} aria-hidden />
                INTERNAL_ERROR
              </div>

              <div className="relative inline-block">
                <h1
                  className="text-[8rem] sm:text-[10rem] lg:text-[12rem] font-black tracking-tighter leading-none bg-clip-text text-transparent bg-gradient-to-br from-rose-600 via-orange-600 to-rose-800 dark:from-rose-400 dark:via-orange-400 dark:to-rose-600 select-none"
                  aria-label={`${t('code')} — ${t('title')}`}
                >
                  {t('code')}
                </h1>
                <div
                  aria-hidden
                  className="absolute inset-0 text-[8rem] sm:text-[10rem] lg:text-[12rem] font-black tracking-tighter leading-none text-rose-500/10 dark:text-rose-400/10 blur-md select-none translate-x-2 translate-y-2 -z-10"
                >
                  {t('code')}
                </div>
              </div>
            </div>

            <div className="space-y-5">
              {/* Terminal — error stack snippet */}
              <div className="font-mono text-xs sm:text-sm bg-slate-900/95 dark:bg-slate-950 border border-slate-800 dark:border-slate-700 rounded-lg p-4 shadow-2xl">
                <div className="flex items-center gap-2 mb-3 pb-3 border-b border-slate-800 dark:border-slate-700">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-wider ml-2">
                    <Terminal size={10} aria-hidden />
                    error-trace
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="text-rose-400">
                    {'>'} UncaughtError: internal_server_error
                  </div>
                  <div className="text-slate-500">
                    at <span className="text-blue-400">securesys.runtime</span>
                  </div>
                  {error.digest && (
                    <div className="text-amber-400 text-[10px] sm:text-xs break-all">
                      digest: {error.digest}
                    </div>
                  )}
                  <div className="text-slate-500">
                    <span className="text-rose-400">→</span> exit code: 500
                    <span className="inline-block w-2 h-4 bg-rose-400 ml-2 animate-pulse align-middle" />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 mb-3">
                  {t('title')}
                </h2>
                <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                  {t('subtitle')}
                </p>
              </div>

              {/* Aksiyon butonları */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => reset()}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white px-6 py-3 text-sm font-semibold shadow-lg shadow-rose-500/30 hover:shadow-xl hover:shadow-rose-500/40 transition-all"
                >
                  <RotateCw size={16} aria-hidden />
                  {t('retry')}
                </button>
                <Link
                  href="/"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200 px-6 py-3 text-sm font-semibold transition-colors"
                >
                  <Home size={16} aria-hidden />
                  {t('back_home')}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Footer hint */}
        <div className="mt-12 flex items-center justify-center">
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-full border border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm">
            <Mail size={14} className="text-slate-500" aria-hidden />
            <span className="text-sm text-slate-600 dark:text-slate-400">
              {t('report_hint')}
            </span>
            <Link
              href="/iletisim"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            >
              <ArrowRight size={14} aria-hidden />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
